import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    SchedulingType,
    DayOfWeek,
    Month,
    TwoMonths,
    Quarter,
    HalfYear,
    OffsetAnchor,
    OffsetType,
} from "api/generated/model";

import { ControlSchedulingType } from "../types";

/*
 * Control scheduling type
 */

function schedulingTypeToControlSchedulingType(
    schedulingType: SchedulingType
): ControlSchedulingType {
    switch (schedulingType) {
        case "Manual":
            return "Date";
        default:
            return schedulingType;
    }
}

function controlSchedulingTypeToSchedulingType(
    controlSchedulingType: ControlSchedulingType
): SchedulingType {
    switch (controlSchedulingType) {
        case "Date":
            return "Manual";
        default:
            return controlSchedulingType;
    }
}

/*
 * Control program status
 */

type ControlProgramStatus = "Active" | "Inactive";

function formatControlProgramStatus(status: ControlProgramStatus): string {
    switch (status) {
        case "Active":
            return t("ViewControlProgram_Status_Active");
        case "Inactive":
            return t("ViewControlProgram_Status_Inactive");
        default:
            return assertNever(status);
    }
}

function unformatControlProgramStatus(status: string): ControlProgramStatus {
    switch (status) {
        case t("ViewControlProgram_Status_Active"):
            return "Active";
        case t("ViewControlProgram_Status_Inactive"):
            return "Inactive";
        default:
            throw new Error("Unexpected formatted status: " + status);
    }
}

const CONTROL_PROGRAM_STATUS_MATCH_TYPE = "ControlProgramStatus";

/*
 * Control login protection
 */

type ControlLoginProtection = "Required" | "NotRequired";

function formatControlLoginProtection(
    loginProtection: ControlLoginProtection
): string {
    switch (loginProtection) {
        case "Required":
            return t("LoginProtection_Required");
        case "NotRequired":
            return t("LoginProtection_NotRequired");
        default:
            return assertNever(loginProtection);
    }
}

function unformatControlLoginProtection(
    loginProtection: string
): ControlLoginProtection {
    switch (loginProtection) {
        case t("LoginProtection_Required"):
            return "Required";
        case t("LoginProtection_NotRequired"):
            return "NotRequired";
        default:
            throw new Error(
                "Unexpected formatted loginProtection: " + loginProtection
            );
    }
}

/*
 * Control segregation of duties
 */

type ControlSegregationOfDuties = "Enforced" | "NotEnforced";

function formatControlSegregationOfDuties(
    sod: ControlSegregationOfDuties
): string {
    switch (sod) {
        case "Enforced":
            return t("SegregationOfDuties_Enforced");
        case "NotEnforced":
            return t("SegregationOfDuties_NotEnforced");
        default:
            return assertNever(sod);
    }
}

function unformatControlSegregationOfDuties(
    sod: string
): ControlSegregationOfDuties {
    switch (sod) {
        case t("SegregationOfDuties_Enforced"):
            return "Enforced";
        case t("SegregationOfDuties_NotEnforced"):
            return "NotEnforced";
        default:
            throw new Error(
                "Unexpected formatted segregation of duties: " + sod
            );
    }
}

function translateDayOfWeek(day: DayOfWeek): string {
    switch (day) {
        case "Monday":
            return t("Monday");
        case "Tuesday":
            return t("Tuesday");
        case "Wednesday":
            return t("Wednesday");
        case "Thursday":
            return t("Thursday");
        case "Friday":
            return t("Friday");
        case "Saturday":
            return t("Saturday");
        case "Sunday":
            return t("Sunday");
        default:
            return assertNever(day);
    }
}

function translateMonth(month: Month): string {
    switch (month) {
        case "January":
            return t("Month_1");
        case "February":
            return t("Month_2");
        case "March":
            return t("Month_3");
        case "April":
            return t("Month_4");
        case "May":
            return t("Month_5");
        case "June":
            return t("Month_6");
        case "July":
            return t("Month_7");
        case "August":
            return t("Month_8");
        case "September":
            return t("Month_9");
        case "October":
            return t("Month_10");
        case "November":
            return t("Month_11");
        case "December":
            return t("Month_12");
        default:
            return assertNever(month);
    }
}

function translateMonthShort(month: Month): string {
    switch (month) {
        case "January":
            return t("ShortMonth_January");
        case "February":
            return t("ShortMonth_February");
        case "March":
            return t("ShortMonth_March");
        case "April":
            return t("ShortMonth_April");
        case "May":
            return t("ShortMonth_May");
        case "June":
            return t("ShortMonth_June");
        case "July":
            return t("ShortMonth_July");
        case "August":
            return t("ShortMonth_August");
        case "September":
            return t("ShortMonth_September");
        case "October":
            return t("ShortMonth_October");
        case "November":
            return t("ShortMonth_November");
        case "December":
            return t("ShortMonth_December");
        default:
            return assertNever(month);
    }
}

function translateTwoMonthsPeriod(period: TwoMonths): string {
    switch (period) {
        case "JanuaryFebruary":
            return t("TwoMonth_1");
        case "MarchApril":
            return t("TwoMonth_2");
        case "MayJune":
            return t("TwoMonth_3");
        case "JulyAugust":
            return t("TwoMonth_4");
        case "SeptemberOctober":
            return t("TwoMonth_5");
        case "NovemberDecember":
            return t("TwoMonth_6");
        default:
            return assertNever(period);
    }
}

function translateQuarter(quarter: Quarter): string {
    switch (quarter) {
        case "First":
            return t("Quarter_1");
        case "Second":
            return t("Quarter_2");
        case "Third":
            return t("Quarter_3");
        case "Fourth":
            return t("Quarter_4");
        default:
            return assertNever(quarter);
    }
}

function translateHalfYear(half: HalfYear): string {
    switch (half) {
        case "First":
            return t("Half_1");
        case "Second":
            return t("Half_2");
        default:
            return assertNever(half);
    }
}

function translateRecurrenceWeeklyPeriodicity(periodicity: number): string {
    switch (periodicity) {
        case 1:
            return t("CreateControl_Reccurence_Weekly_Periodicity_1");
        case 2:
            return t("CreateControl_Reccurence_Weekly_Periodicity_2");
        case 3:
            return t("CreateControl_Reccurence_Weekly_Periodicity_3");
        case 4:
            return t("CreateControl_Reccurence_Weekly_Periodicity_4");
        case 5:
            return t("CreateControl_Reccurence_Weekly_Periodicity_5");
        case 6:
            return t("CreateControl_Reccurence_Weekly_Periodicity_6");
        default:
            throw new Error(
                `There is no translation for weekly periodicity = ${periodicity}`
            );
    }
}

function translateOffsetAnchor(anchor: OffsetAnchor): string {
    switch (anchor) {
        case "StartOfInterval":
            return t("CreateControl_Recurrence_DateRule_Beginning");
        case "EndOfInterval":
            return t("CreateControl_Recurrence_DateRule_End");
        default:
            return assertNever(anchor);
    }
}

function translateOffsetType(offsetType: OffsetType): string {
    switch (offsetType) {
        case "AfterAnchor":
            return t("CreateControl_Recurrence_DateRule_After");
        case "BeforeAnchor":
            return t("CreateControl_Recurrence_DateRule_Before");
        default:
            assertNever(offsetType);
    }
}

export {
    type ControlSchedulingType,
    type ControlProgramStatus,
    formatControlProgramStatus,
    unformatControlProgramStatus,
    CONTROL_PROGRAM_STATUS_MATCH_TYPE,
    type ControlLoginProtection,
    formatControlLoginProtection,
    unformatControlLoginProtection,
    type ControlSegregationOfDuties,
    formatControlSegregationOfDuties,
    unformatControlSegregationOfDuties,
    translateMonth,
    translateMonthShort,
    translateTwoMonthsPeriod,
    translateDayOfWeek,
    translateRecurrenceWeeklyPeriodicity,
    translateQuarter,
    translateHalfYear,
    translateOffsetAnchor,
    translateOffsetType,
    schedulingTypeToControlSchedulingType,
    controlSchedulingTypeToSchedulingType,
};
